import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { useTheme, type Theme } from "@/hooks/useTheme";

const THEMES: { value: Theme; label: string; desc: string; preview: ThemePreviewProps }[] = [
  {
    value: "dark",
    label: "Scuro",
    desc: "Tema predefinito",
    preview: {
      bg: "#0d0d1a",
      card: "#12122a",
      border: "#2a2a4a",
      primary: "#e8e8ff",
      secondary: "#aaaacc",
      accent: "#E8613C",
    },
  },
  {
    value: "light",
    label: "Chiaro",
    desc: "Tema luminoso",
    preview: {
      bg: "#f5f5f8",
      card: "#ffffff",
      border: "#e0e0ec",
      primary: "#1a1a2e",
      secondary: "#4a4a6a",
      accent: "#E8613C",
    },
  },
];

export default function Settings() {
  const { theme, setTheme } = useTheme();

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <header className="flex justify-between items-baseline">
        <h1 className="text-3xl font-serif text-primary">Impostazioni</h1>
        <Link to="/admin/dashboard" className="font-mono text-[17px] text-secondary hover:text-primary">
          ← dashboard
        </Link>
      </header>

      <Card>
        <h2 className="font-mono text-xs uppercase tracking-wider text-secondary mb-4">
          Tema dell'interfaccia
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {THEMES.map((t) => (
            <button
              key={t.value}
              onClick={() => setTheme(t.value)}
              className={`
                text-left rounded-xl border-2 p-3 transition-colors
                ${theme === t.value
                  ? "border-accent"
                  : "border-border hover:border-secondary"}
              `}
            >
              <ThemePreview {...t.preview} />
              <div className="mt-3 flex items-center gap-2">
                <span
                  className={`w-3 h-3 rounded-full border-2 shrink-0 ${
                    theme === t.value ? "border-accent bg-accent" : "border-border bg-transparent"
                  }`}
                />
                <div>
                  <div className="text-primary font-mono text-sm">{t.label}</div>
                  <div className="text-muted font-mono text-xs">{t.desc}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

interface ThemePreviewProps {
  bg: string;
  card: string;
  border: string;
  primary: string;
  secondary: string;
  accent: string;
}

function ThemePreview({ bg, card, border, primary, secondary, accent }: ThemePreviewProps) {
  return (
    <div
      className="rounded-lg p-3 space-y-2"
      style={{ backgroundColor: bg, border: `1px solid ${border}` }}
    >
      {/* Barra top simulata */}
      <div className="flex items-center gap-1.5">
        <div className="w-10 h-1.5 rounded-full" style={{ backgroundColor: secondary, opacity: 0.4 }} />
        <div className="w-6 h-1.5 rounded-full" style={{ backgroundColor: secondary, opacity: 0.25 }} />
      </div>
      {/* Card simulata */}
      <div
        className="rounded p-2 space-y-1.5"
        style={{ backgroundColor: card, border: `1px solid ${border}` }}
      >
        <div className="w-16 h-1.5 rounded-full" style={{ backgroundColor: primary, opacity: 0.8 }} />
        <div className="w-12 h-1 rounded-full" style={{ backgroundColor: secondary, opacity: 0.5 }} />
        <div
          className="mt-2 rounded px-2 py-1 w-fit"
          style={{ backgroundColor: accent }}
        >
          <div className="w-8 h-1 rounded-full bg-white opacity-80" />
        </div>
      </div>
    </div>
  );
}
